/**
 * Preparing a form: the page's fields as boxes to select, move and resize,
 * and detected suggestions to accept or dismiss.
 */

import { useRef, useState } from 'react'
import type { FieldSuggestion, FormField } from '../api'
import { unrotateDelta } from './rotation'

interface Props {
  fields: FormField[]
  /** The page's /Rotate; drags arrive in screen axes. */
  rotation?: number
  suggestions: FieldSuggestion[]
  pageIndex: number
  originX: number
  originY: number
  pageHeight: number
  scale: number
  selected: string | null
  onSelect: (name: string | null) => void
  /** A widget moved or resized, in PDF user space. */
  onGeometry: (name: string, widget: number, rect: [number, number, number, number]) => void
  onAccept: (index: number) => void
  onDismiss: (index: number) => void
}

type Drag = { name: string; widget: number; mode: 'move' | 'resize'; x: number; y: number; rect: number[] }

/** Capture the pointer for a drag; never let a failure end the drag. */
function capture(element: Element | null | undefined, pointerId: number): void {
  try {
    element?.setPointerCapture?.(pointerId)
  } catch {
    // Not a live pointer (synthetic events): the drag still works without it.
  }
}

export function PrepareLayer(props: Props) {
  const { scale, originX, originY, pageHeight } = props
  const [drag, setDrag] = useState<Drag | null>(null)
  const [offset, setOffset] = useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 })
  const moved = useRef(false)

  const place = (r: number[], extra?: { dx: number; dy: number; resize: boolean }) => {
    const [x0, y0, x1, y1] = r
    const left = (x0 - originX) * scale
    const top = (pageHeight - (y1 - originY)) * scale
    let width = (x1 - x0) * scale
    let height = (y1 - y0) * scale
    if (!extra) return { left, top, width, height }
    if (extra.resize) {
      width = Math.max(8, width + extra.dx)
      height = Math.max(8, height + extra.dy)
      return { left, top, width, height }
    }
    return { left: left + extra.dx, top: top + extra.dy, width, height }
  }

  const commit = () => {
    if (!drag) return
    const { dx, dy } = offset
    setDrag(null)
    setOffset({ dx: 0, dy: 0 })
    if (!moved.current) return
    const [x0, y0, x1, y1] = drag.rect
    const ux = dx / scale
    const uy = -dy / scale
    const rect: [number, number, number, number] = drag.mode === 'move'
      ? [x0 + ux, y0 + uy, x1 + ux, y1 + uy]
      : [x0, Math.min(y1 - 4, y0 + uy), Math.max(x0 + 4, x1 + ux), y1]
    props.onGeometry(drag.name, drag.widget, rect)
  }

  return (
    <div
      className="page__layer prepare-layer"
      onPointerMove={(event) => {
        if (!drag) return
        moved.current = true
        const turned = unrotateDelta(props.rotation ?? 0, event.clientX - drag.x, event.clientY - drag.y)
        setOffset({ dx: turned.x, dy: turned.y })
      }}
      onPointerUp={commit}
      onClick={(event) => { if (event.target === event.currentTarget) props.onSelect(null) }}
    >
      {props.fields.flatMap((field) =>
        field.widgets.map((w, i) => ({ field, w, i })).filter(({ w }) => w.pageIndex === props.pageIndex)
          .map(({ field, w, i }) => {
            const active = drag && drag.name === field.name && drag.widget === i
            const style = place(w.rect, active ? { ...offset, resize: drag!.mode === 'resize' } : undefined)
            return (
              <div
                key={`${field.name}:${i}`}
                className="prep-field"
                data-kind={field.kind}
                data-selected={props.selected === field.name || undefined}
                style={style}
                title={`${field.name} (${field.kind})`}
                onPointerDown={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  capture(event.currentTarget.parentElement, event.pointerId)
                  moved.current = false
                  props.onSelect(field.name)
                  setDrag({ name: field.name, widget: i, mode: 'move', x: event.clientX, y: event.clientY, rect: w.rect })
                }}
              >
                <span className="prep-field__name">{field.name}</span>
                <span
                  className="prep-field__handle"
                  title="Drag to resize"
                  onPointerDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    capture(event.currentTarget.closest('.prepare-layer'), event.pointerId)
                    moved.current = false
                    props.onSelect(field.name)
                    setDrag({ name: field.name, widget: i, mode: 'resize', x: event.clientX, y: event.clientY, rect: w.rect })
                  }}
                />
              </div>
            )
          }),
      )}
      {props.suggestions.map((s, i) => (
        <div key={`s${i}`} className="prep-suggestion" data-kind={s.kind} style={place(s.rect)}
             title={`Suggested ${s.kind} field "${s.name}" — ${s.reason}`}>
          <span className="prep-suggestion__actions">
            <button title="Add this field" onClick={(e) => { e.stopPropagation(); props.onAccept(i) }}>{'✓'}</button>
            <button title="Dismiss" onClick={(e) => { e.stopPropagation(); props.onDismiss(i) }}>{'✕'}</button>
          </span>
        </div>
      ))}
    </div>
  )
}
